import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

const InvestorSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section 
      id="investor" 
      className="py-24 md:py-32 relative" 
      ref={ref} 
      data-testid="investor-section"
      style={{ background: '#050505' }}
    >
      {/* Subtle glow */}
      <div 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] rounded-full blur-[300px] opacity-10"
        style={{ background: 'radial-gradient(ellipse, #c9a227 0%, transparent 70%)' }}
      />
      
      <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="mb-16 text-center"
        >
          <h2 
            className="text-3xl md:text-4xl lg:text-5xl font-light tracking-[0.2em] text-white mb-4"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            Investor Invitation
          </h2>
          <div className="w-24 h-[1px] bg-[#c9a227]/50 mx-auto" />
        </motion.div>

        {/* Investor Dossier Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-12 text-center"
        >
          <h3 
            className="text-xl md:text-2xl font-light tracking-wider text-[#c9a227] mb-4"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            CELESTIAL MINDS FZE — INVESTOR DOSSIER
          </h3>
          <p className="text-white/60 italic">A Civilisation-Scale Innovation Studio</p>
        </motion.div>

        {/* Introduction */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <div className="space-y-4 text-white/70 leading-relaxed">
            <p>
              Celestial Minds FZE is an advanced innovation studio structured to design, incubate, and selectively deploy high-impact systems addressing long-horizon challenges across technology, education, governance, infrastructure, and human advancement.
            </p>
            <p>
              The organisation is built around a single, disciplined premise:
            </p>
            <p className="text-[#c9a227] text-lg italic text-center py-4">
              Civilisations progress through systems — not isolated products.
            </p>
            <p>
              Celestial Minds focuses on architecting those systems early, rigorously, and responsibly, before they fragment into disconnected solutions.
            </p>
            <p>
              Celestial Minds is not a traditional operating company. It functions as a strategic architecture and venture-generation platform, combining multidisciplinary research, system design, and selective commercialisation pathways.
            </p>
          </div>
        </motion.div>

        <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#c9a227]/30 to-transparent mb-12" />

        {/* The Value Thesis */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <h3 
            className="text-xl font-light tracking-wider text-[#c9a227] mb-8"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            The Value Thesis
          </h3>
          <p className="text-white/70 mb-6">
            Celestial Minds is built around three core pillars that institutional investors consistently recognise as long-term value drivers.
          </p>

          {/* Pillar 1 */}
          <div className="mb-8 pl-6 border-l-2 border-[#c9a227]/30">
            <h4 className="text-lg text-white mb-3" style={{ fontFamily: "'Cinzel', serif" }}>1. Dense Intellectual Capital</h4>
            <p className="text-white/70 leading-relaxed">
              Celestial Minds develops structured frameworks and system architectures across multiple future-facing domains. Rather than pursuing single-point solutions, the organisation builds modular intellectual assets that can be adapted, licensed, or selectively spun out across multiple markets. This approach creates optionality, capital efficiency, and compounding value over time.
            </p>
          </div>

          {/* Pillar 2 */}
          <div className="mb-8 pl-6 border-l-2 border-[#c9a227]/30">
            <h4 className="text-lg text-white mb-3" style={{ fontFamily: "'Cinzel', serif" }}>2. Cross-Vertical Applicability</h4>
            <p className="text-white/70 leading-relaxed mb-4">
              The systems designed within Celestial Minds are intentionally cross-sector by design. They address foundational needs — trust, learning, coordination, access, and efficiency — that recur across industries.
            </p>
            <p className="text-white/70">This enables: defensibility through abstraction, scalability across markets, diversified revenue pathways, and reduced dependency on single-sector outcomes.</p>
          </div>

          {/* Pillar 3 */}
          <div className="pl-6 border-l-2 border-[#c9a227]/30">
            <h4 className="text-lg text-white mb-3" style={{ fontFamily: "'Cinzel', serif" }}>3. Founder-Led Strategic Architecture</h4>
            <p className="text-white/70 leading-relaxed">
              Celestial Minds is founder-led, with strategy, synthesis, and long-horizon thinking centralised at the architectural level. The founder approaches markets not as static categories, but as evolving systems shaped by incentives, technology, and human behaviour.
            </p>
          </div>
        </motion.div>

        <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#c9a227]/30 to-transparent mb-12" />

        {/* The Celestial Minds Advantage */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <h3 
            className="text-xl font-light tracking-wider text-[#c9a227] mb-6"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            The Celestial Minds Advantage
          </h3>
          <p className="text-white/70 mb-4">Celestial Minds is intentionally designed to:</p>
          <ul className="space-y-2 text-white/70">
            {[
              'Operate globally from inception',
              'Generate multiple venture pathways from shared architectures',
              'Engage with private capital, institutions, and sovereign entities',
              'Maintain strategic flexibility rather than fixed product dependency',
              'Balance societal value with durable commercial outcomes',
              'Remain ahead of market convergence rather than react to it'
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="text-[#c9a227] mt-1">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <p className="text-white/60 italic mt-6">
            This is not acceleration for its own sake. It is measured, system-first progress.
          </p>
        </motion.div>

        <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#c9a227]/30 to-transparent mb-12" />

        {/* Purpose */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <h3 
            className="text-xl font-light tracking-wider text-[#c9a227] mb-6"
            style={{ fontFamily: "'Cinzel', serif" }}
          >
            Purpose
          </h3>
          <div className="space-y-4 text-white/70 leading-relaxed max-w-3xl mx-auto">
            <p>
              Celestial Minds exists to advance human potential — intellectually, economically, technologically, and socially — through disciplined system design and thoughtful innovation.
            </p>
            <p className="text-white/90">
              For aligned investors, participation is not merely about equity exposure. It is about positioning alongside a platform designed to operate at generational scale.
            </p>
          </div>
          <button 
            onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="mt-10 px-8 py-3 bg-[#c9a227] text-[#050505] text-xs uppercase tracking-widest hover:bg-[#e8d5a3] transition-colors"
            data-testid="investor-contact-btn"
          >
            Request Information
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default InvestorSection;
